<template>
  <div class="container-custom moo-reward-page" :style="styleObject">
    <div class="content-custom">
      <div v-if="isLoadDone">
        <div v-if="isLogined">
          <div class="reward-sheet">
            <!-- Brand badge overlapping the sheet top edge -->
            <div class="reward-sheet-logo">
              <img src="../assets/img/collect-reward.png" alt="reward" />
            </div>

            <!-- Header: title + close -->
            <div class="reward-sheet-head">
              <div class="reward-sheet-head-text">
                <div class="reward-title-row">
                  <span class="material-icons-round reward-title-icon"
                    >celebration</span
                  >
                  <h2 class="reward-title">{{ $t('rewards.modalTitle') }}</h2>
                </div>
                <p class="reward-subtitle">
                  {{ $t('rewards.subtitleQueue') }}
                </p>
              </div>
              <button class="reward-close" aria-label="Close" @click="goBack()">
                <span class="material-icons-round">close</span>
              </button>
            </div>

            <!-- MTH108: Reward Catalogue CTA switched back on — MTH082 had
                 only commented it out, so re-enabling is the same markup that
                 shipped before. Opens RewardCatalogue (redeem points/wallet
                 balance into a voucher, which then lands back in this list). -->
            <div class="reward-catalogue-wrap">
              <div class="btn-reward-catalogue" @click="gotoRewardCatalogue()">
                <span
                  class="material-icons-round"
                  style="font-size: 18px; vertical-align: middle"
                  >redeem</span
                >
                {{ $t('rewards.catalogue') }}
              </div>
            </div>

            <search-input
              :onChangesearchKeyword="onChangesearchKeyword"
              :placeholder="$t('rewards.searchPlaceholder')"
              class="reward-search"
            />

            <!-- MTH090: the table's bill already carries a voucher -->
            <div v-if="voucherLockedByTable" class="reward-not-allowed-banner">
              <span class="material-icons-round">block</span>
              {{ $t('rewards.voucherLockedTable', { name: lockedVoucherLabel }) }}
            </div>

            <!-- SF231: branch disallows applying vouchers to the order -->
            <div v-if="voucherNotAllowedForOutlet" class="reward-not-allowed-banner">
              <span class="material-icons-round">block</span>
              {{ $t('rewards.voucherNotAllowedOutlet') }}
            </div>

            <!-- Voucher list -->
            <div class="reward-list" v-if="promocodeData.length">
              <div
                v-for="(entry, index) in stackedRewards"
                :key="index"
                class="reward-card"
                :class="[
                  rewardTintClass(entry.item),
                  {
                    'reward-card--ineligible': isEntryDisabled(entry),
                  },
                ]"
              >
                <div class="reward-card-row">
                  <span class="reward-chip">{{ $t('rewards.voucherCategory') }}</span>
                  <button
                    v-if="accept_payment_on_moo"
                    type="button"
                    class="reward-use"
                    :class="{
                      'reward-use--applied': isApplied(entry.item),
                      'reward-use--ineligible': isEntryDisabled(entry),
                    }"
                    :disabled="isEntryDisabled(entry)"
                    @click="handleUseClick(entry.item)"
                  >
                    {{ isApplied(entry.item) ? $t('rewards.remove') : $t('rewards.useNow') }}
                  </button>
                </div>

                <div class="reward-card-text">
                  <h3 class="reward-name">{{ entry.item.reward_title ?? "" }}</h3>
                  <p v-if="entry.item.bundle_name" class="reward-meta">
                    {{ $t('rewards.bundle', { name: entry.item.bundle_name }) }}
                  </p>
                  <!-- MTH086: stacked card — say how many of the N vouchers are
                       still usable vs locked, so a locked one no longer makes
                       the whole stack look unavailable. -->
                  <p v-if="entry.count > 1" class="reward-stack-availability">
                    {{
                      $t('rewards.stackAvailability', {
                        available: entry.usableCount,
                        locked: entry.lockedCount,
                      })
                    }}
                  </p>
                  <!-- MTH091: the card already prints "Min. Spending: $X" in its
                       T&C; when the cart hasn't reached it, say how much more is
                       needed instead of leaving Use Now silently tappable. -->
                  <p
                    v-if="minSpendShortfall(entry.item) > 0 && !isApplied(entry.item)"
                    class="reward-min-spend-note"
                  >
                    {{
                      $t('rewards.minSpendNotMet', {
                        amount: formatAmount(minSpendShortfall(entry.item)),
                      })
                    }}
                  </p>
                </div>

                <div
                  class="reward-tnc"
                  v-if="
                    rewardTncLines(entry.item).length ||
                    entry.item.terms_conditions
                  "
                >
                  <span class="reward-tnc-label">{{ $t('rewards.tnc') }}</span>
                  <ul
                    class="reward-tnc-list"
                    v-if="rewardTncLines(entry.item).length"
                  >
                    <li
                      v-for="(line, li) in rewardTncLines(entry.item)"
                      :key="li"
                    >
                      {{ line }}
                    </li>
                  </ul>
                  <div
                    v-if="entry.item.terms_conditions"
                    class="reward-tnc-more"
                    @click="openTermAndConditions(entry.item.terms_conditions)"
                  >
                    {{ $t('rewards.viewTnc') }}
                  </div>
                </div>

                <span v-if="entry.count > 1" class="reward-stack-badge"
                  >x{{ entry.count }}</span
                >
              </div>
            </div>
            <div class="reward-empty" v-else>{{ $t('rewards.noRewards') }}</div>

            <!-- Update profile -->
            <div
              class="root_row"
              v-if="currentMember"
              @click="showUpdateProfile()"
            >
              <div class="root_row_child">
                <span
                  class="material-icons-round opacity-10 cursor-pointer text-center active md-30"
                  >person</span
                >
              </div>
              <div class="root_row_child_text">{{ $t('rewards.updateProfile') }}</div>
              <div class="root_row_child_icon">
                <span
                  class="material-icons-round opacity-10 cursor-pointer text-center active"
                  >navigate_next</span
                >
              </div>
            </div>
          </div>
        </div>
        <!-- MTH052: a guest has no member session and therefore no rewards.
             Instead of a bare sign-in link, invite them to log in / register
             with the same prompt Advocado shows (SF223). -->
        <!-- MTH075: markup moved into GuestRewardPrompt so MyCart shows the
             identical card. Dismissing here still navigates back. -->
        <GuestRewardPrompt
          v-else
          :show="showGuestPrompt"
          @signin="gotoSignIn()"
          @dismiss="dismissGuestPrompt()"
        />
      </div>
    </div>
    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <div>
      <button
        style="display: none"
        type="button"
        class="btn-term-and-conditions-modal"
        data-bs-toggle="modal"
        data-bs-target="#modalShowTermAndConditions"
      ></button>
      <div
        class="modal fade moo-bottom-sheet"
        id="modalShowTermAndConditions"
        tabindex="-1"
        aria-labelledby="modalShowTermAndConditionsLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog">
          <div class="modal-content rw-terms">
            <div class="rw-terms__head">
              <span class="material-icons-round rw-terms__icon">gavel</span>
              <div class="rw-terms__title" id="modalShowTermAndConditionsLabel">
                {{ $t('rewardCatalogue.termsTitle') }}
              </div>
              <button
                type="button"
                class="rw-terms__close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span class="material-icons-round">close</span>
              </button>
            </div>
            <div class="rw-terms__body" v-html="contentTermAndCondition"></div>
            <button type="button" class="rw-terms__done" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ModalUpdateProfile
      v-if="currentMember"
      :keyReload="keyReloadModal"
      :member="currentMember"
      @update-success="
        getList();
        getDataMember;
      "
    />
  </div>
  <ModalSelectFreeItem
    :keyReload="keyReload"
    :rewardId="rewardIdSelect"
    :handleUseVoucher="handleUseVoucher"
    :removeVoucherSelected="removeVoucherSelected"
    :theme2="true"
  />
  <material-loading :active="loaderActive" />
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import PromocodeService from "../services/promocode.service";
import OrderService from "../services/order.service";
import moment from "moment";
import $ from "jquery";
// import VoucherAvailable from "@/common/VoucherAvailable/VoucherAvailable";
import SearchInput from "@/components/SearchInput.vue";
import ModalSelectFreeItem from "@/components/ModalSelectFreeItem.vue";
import GuestRewardPrompt from "@/components/GuestRewardPrompt.vue";
import { gotoMemberLogin, localStorageName } from "@/config/utils";
import { FREE_ITEM } from "@/config/const";
import memberService from "../services/member.service";
import ModalUpdateProfile from "../views/components/ModalUpdateProfile.vue";

export default {
  name: "sign-in",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    // VoucherAvailable,
    SearchInput,
    ModalSelectFreeItem,
    ModalUpdateProfile,
    GuestRewardPrompt,
  },
  data() {
    return {
      isLoadDone: false,
      snackbar: null,
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      cutleryValue: 1,
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("memberInfo"), "")
      ),
      loaderActive: false,
      searchKeyword: "",
      progressPercentage: 10, // Set the initial percentage here
      progressBarColor: `linear-gradient(90deg, red 50%, gray 50%)`,
      contentTermAndCondition: "",
      selectedReward: [],
      promocodeData: [],
      message: "",
      searchTimerId: null,
      fromCart: this.$route.params.from_cart ?? false,
      fromOrderHistory: this.$route.params.from_order_history ?? false,
      isLogined: false,
      // MTH052: guest log-in prompt shown in place of the rewards list.
      showGuestPrompt: false,
      accept_payment_on_moo: true,
      tableInfo: null,
      rewardIdSelect: 0,
      keyReload: "keyReload",
      freeItem: FREE_ITEM,
      keyReloadModal: "reload",
      currentMember: null,
      // MTH091: subtotal of the cart this page was opened from, used to gate
      // vouchers that carry a minimum spend.
      cartSubtotal: 0,
      // MTH090: voucher this pay-later table session is already locked to.
      tableVoucherLock: null,
    };
  },
  watch: {
    progressPercentage() {
      this.updateProgressBarColor();
    },
  },
  async created() {
    const merchantCode = this.$store.state.merchantCode;
    const branchCode = this.$store.state.branchCode;
    const tableCode = this.$route.params.table_code ?? "";
    var memberInfoData = JSON.parse(
      localStorage.getItem(localStorageName("memberInfo"), "")
    );
    this.tableInfo = JSON.parse(
      localStorage.getItem(
        "table_" +
          merchantCode.toLowerCase() +
          "_" +
          branchCode.toLowerCase() +
          "_" +
          tableCode.toLowerCase(),
        null
      )
    );

    await this.loadCartSubtotal();

    if (memberInfoData) {
      this.isLogined = true;
      this.isLoadDone = false;
      await this.getList();
      await this.getDataMember();
      this.isLoadDone = true;
    } else {
      this.isLogined = false;
      this.isLoadDone = true;
      // MTH052: open the guest prompt as soon as the page settles.
      this.showGuestPrompt = true;
    }

    if (this.tableInfo) {
      this.accept_payment_on_moo =
        this.tableInfo.accept_payment_on_moo ?? false;

      // MTH090: the voucher lock belongs to the pay-later flow only. A pay-first
      // table (stripe_connect) pays per order, so its vouchers stay selectable —
      // same rule MyCart uses to choose saveOrder vs saveOrderQR.
      if (!this.tableInfo.stripe_connect) {
        await this.getTableVoucherLock(branchCode, tableCode);
      }
    }
  },
  computed: {
    styleObject() {
      // MTH035: drive the reskin off the merchant's Theme 2 colour
      // (main_theme2_color), same base Order.vue / the bottom nav use — NOT
      // the legacy moo_color. All the --rw-* tints color-mix off var(--color).
      const base =
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a";
      return {
        "--color": base,
      };
    },
    // MTH035: collapse duplicate vouchers (same reward) into one card carrying
    // an "xN" count, mirroring the Advocado VouchersModal. Applied instances
    // are kept as their own entry so the Applied/Remove state stays pinned to
    // the exact voucher_id the member selected.
    // MTH086: `locked` is per member-voucher instance, so a stack of N can mix
    // spent and usable vouchers — a voucher is spent when it is locked OR
    // already applied to this bill. The stacked card's count and its
    // "3 available · 1 locked" line are therefore computed over EVERY instance of
    // the reward (the applied one included, even though it also renders as its
    // own Remove card), and the representative `item` is a voucher that can
    // actually be applied, so both the enabled state and handleUseClick target
    // a usable voucher_id instead of whichever instance happened to come first.
    stackedRewards() {
      const list = Array.isArray(this.promocodeData) ? this.promocodeData : [];
      const keyOf = (item) =>
        item && item.id != null
          ? `id:${item.id}`
          : `name:${(item && item.reward_title) || ""}`;
      const isUsable = (item) =>
        !(item && item.isVoucherLocked) && !this.isApplied(item);

      const totals = new Map();
      list.forEach((item) => {
        const key = keyOf(item);
        const total = totals.get(key) || { count: 0, usableCount: 0 };
        total.count += 1;
        if (isUsable(item)) {
          total.usableCount += 1;
        }
        totals.set(key, total);
      });

      const entries = [];
      const stackIndexByKey = new Map();
      list.forEach((item) => {
        if (this.isApplied(item)) {
          entries.push({ item, count: 1, usableCount: 0, lockedCount: 1 });
          return;
        }
        const key = keyOf(item);
        if (!stackIndexByKey.has(key)) {
          const total = totals.get(key);
          stackIndexByKey.set(key, entries.length);
          entries.push({
            item,
            count: total.count,
            usableCount: total.usableCount,
            lockedCount: total.count - total.usableCount,
          });
          return;
        }
        const entry = entries[stackIndexByKey.get(key)];
        if (!isUsable(entry.item) && isUsable(item)) {
          entry.item = item;
        }
      });
      return entries;
    },
    // MTH090: this table's bill already has a voucher on it. One voucher per
    // pay-later session, so nothing on this page can be applied — the backend
    // ignores a second one anyway.
    voucherLockedByTable() {
      return !!(this.tableVoucherLock && this.tableVoucherLock.voucherLocked);
    },
    lockedVoucherLabel() {
      return (this.tableVoucherLock && this.tableVoucherLock.voucherLabel) || "";
    },
    // SF231: branch toggle — vouchers cannot be applied to the order at this
    // outlet. Cashback earning and voucher viewing are unaffected.
    voucherNotAllowedForOutlet() {
      return !!this.branch && parseInt(this.branch.allow_apply_voucher) === 0;
    },
  },
  methods: {
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    updateProgressBarColor() {
      this.progressBarColor = `linear-gradient(90deg, red ${this.progressPercentage}%, gray ${this.progressPercentage}%)`;
    },
    goBack() {
      this.$router.go(-1);
    },
    gotoRewardCatalogue() {
      this.$router.push({
        name: "RewardCatalogue",
        params: this.$route.params,
      });
    },
    gotoSignIn() {
      this.showGuestPrompt = false;
      gotoMemberLogin("myreward", false, "login");
    },
    // MTH052: 'Maybe later' / X / backdrop — close the prompt and go back to
    // the screen the guest came from.
    dismissGuestPrompt() {
      this.showGuestPrompt = false;
      this.goBack();
    },
    // MTH035: is this reward the one currently applied to the cart?
    isApplied(item) {
      return !!(
        this.selectedReward &&
        item &&
        this.selectedReward.voucher_id == item.voucher_id
      );
    },
    // MTH086: a stacked card is usable while it still holds at least one
    // unlocked voucher — not merely when its first instance is unlocked.
    isEntryUsable(entry) {
      return !!(
        !this.voucherNotAllowedForOutlet &&
        !this.voucherLockedByTable &&
        entry &&
        entry.item &&
        entry.item.isCanUseThisBranch &&
        entry.usableCount > 0 &&
        this.meetsMinSpend(entry.item)
      );
    },
    isEntryDisabled(entry) {
      return !this.isEntryUsable(entry) && !this.isApplied(entry && entry.item);
    },
    // MTH035: click on the dark pill — Remove when applied, Use Now otherwise
    // (no-op when the reward can't be used at this branch / is locked).
    handleUseClick(item) {
      if (this.isApplied(item)) {
        this.handleRadioClick(null);
        return;
      }
      if (this.voucherNotAllowedForOutlet || this.voucherLockedByTable) {
        return;
      }
      if (!(item.isCanUseThisBranch && !item.isVoucherLocked)) {
        return;
      }
      // MTH091: never hand a below-minimum cart to the voucher API — the
      // backend rejects it anyway (checkVoucherAlready), and the rejection
      // only surfaces back on the cart as an error modal.
      if (!this.meetsMinSpend(item)) {
        return;
      }
      this.handleRadioClick(item);
    },
    // MTH091: subtotal of the cart the member is holding, read from the same
    // localStorage record MyCart owns (store cartName, plus the queue variant
    // MyCart switches to — the queue flag lives in the cart route's query and
    // is not forwarded here). The Delivery Fee line is deliberately left out:
    // a voucher's minimum spend is about what was ordered, not the fee.
    async loadCartSubtotal() {
      try {
        const baseName = await this.$store.dispatch("cartName");
        const queueName = baseName.replace("ORDER_INFO_", "ORDER_QUEUE_INFO_");
        const readCart = (name) => {
          const raw = localStorage.getItem(name);
          if (!raw) return [];
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        };
        let items = readCart(baseName);
        if (!items.length) {
          items = readCart(queueName);
        }

        this.cartSubtotal = items.reduce((total, product) => {
          if (!product || product.is_delivery_fee_item) return total;
          const unitPrice = Number(
            product.member_price ?? product.price_default ?? 0
          );
          const quantity = Number(product.quantity ?? 0);
          if (!Number.isFinite(unitPrice) || !Number.isFinite(quantity)) {
            return total;
          }
          return total + unitPrice * quantity;
        }, 0);
      } catch (error) {
        // A cart we cannot read must not lock every voucher out.
        this.cartSubtotal = 0;
      }
    },
    // MTH091: the minimum spend the card advertises. `order_total` is nulled by
    // the CRM whenever the voucher is saved with order_minimum = None, so a
    // positive value is exactly the case where the T&C line is printed — the
    // gate and the copy can never disagree.
    minSpendFor(item) {
      const minSpend = Number(item && item.order_total);
      return Number.isFinite(minSpend) && minSpend > 0 ? minSpend : 0;
    },
    meetsMinSpend(item) {
      const minSpend = this.minSpendFor(item);
      return minSpend <= 0 || this.cartSubtotal >= minSpend;
    },
    minSpendShortfall(item) {
      const gap = this.minSpendFor(item) - this.cartSubtotal;
      return gap > 0 ? gap : 0;
    },
    formatAmount(value) {
      const amount = Number(value);
      return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "";
    },
    // MTH035: structured T&C bullet lines shown inside the card (mirrors the
    // Advocado modal: Valid Till → Min. Spending). Built only from fields the
    // backend actually returns.
    rewardTncLines(item) {
      const lines = [];
      if (item && item.expiry_date_text) {
        lines.push(`Valid Till ${item.expiry_date_text}`);
      }
      if (item && item.order_total) {
        lines.push(`Min. Spending: $${item.order_total}`);
      }
      return lines;
    },
    // MTH035: colour-code the card by voucher type, inferred from the title
    // (same heuristic as the Advocado VouchersModal). Same title → same tint.
    rewardTintClass(item) {
      const name = String((item && item.reward_title) || "").toLowerCase();
      if (name.includes("welcome")) return "reward-card--welcome";
      if (name.includes("birthday")) return "reward-card--birthday";
      if (name.includes("digital cash")) return "reward-card--digitalcash";
      const paletteSize = 5;
      let hash = 0;
      for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) % 997;
      }
      return `reward-card--other-${(hash % paletteSize) + 1}`;
    },
    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getList();
      }, 1000);
    },
    async getList() {
      this.showLoader();

      let dataForm = {
        search_keyword: this.searchKeyword,
        type: "",
        merchant_id: localStorage.getItem(localStorageName("MERCHANT_ID"), 0),
        branch_code: this.branch.branch_code,
      };

      await PromocodeService.getMyRewardOrderingOnline(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.promocodeData = response.data.data;

            const storedReward = localStorage.getItem(
              localStorageName("SELECTED_REWARD")
            );
            if (storedReward) {
              var storedRewardConveted = JSON.parse(storedReward);
              if (storedRewardConveted && storedRewardConveted.length > 0) {
                this.selectedReward = storedRewardConveted[0];
              }
            }
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
        }
      );
    },
    // MTH090: read the pay-later session's voucher so the whole list can be
    // locked out — the guest cannot swap the voucher the table's bill already has.
    async getTableVoucherLock(branchCode, tableCode) {
      if (!branchCode || !tableCode) return;

      try {
        const response = await OrderService.getTableVoucherLock({
          branch_code: branchCode,
          table_code: tableCode,
        });

        if (response.data.result.isSuccess) {
          this.tableVoucherLock = response.data.data;
        }
      } catch (e) {
        this.tableVoucherLock = null;
      }
    },
    getFormattedExpirationDate(expirationDate) {
      const now = moment();
      const expiration = moment(expirationDate);
      const daysRemaining = expiration.diff(now, "days");

      if (daysRemaining === 0) {
        return "expired today";
      } else if (daysRemaining === 1) {
        return "expires in 1 day";
      } else {
        return `expires in ${daysRemaining} days`;
      }
    },
    openTermAndConditions(content) {
      this.contentTermAndCondition = content;
      $(".btn-term-and-conditions-modal").click();
    },
    percentageUsed(total, used) {
      return (used / total) * 100;
    },
    handleUseVoucher(itemSelected = null) {
      localStorage.removeItem(localStorageName("SELECTED_REWARD"));
      if (this.selectedReward) {
        this.selectedReward.item_selected = itemSelected;

        localStorage.setItem(
          localStorageName("SELECTED_REWARD"),
          JSON.stringify([this.selectedReward])
        );
        this.$router.go(-1);
      } else {
        this.$router.go(-1);
      }
    },
    handleRadioClick(item) {
      this.rewardIdSelect = 0;
      if (item && item.reward_type == FREE_ITEM) {
        // free item
        this.selectedReward = item;
        this.rewardIdSelect = item.id;
        this.keyReload = Math.random().toString();
        $(".btn-select-free-item-modal").click();
      } else {
        if (this.fromOrderHistory) {
          localStorage.removeItem(
            localStorageName("SELECTED_REWARD_FROM_ORDER_HISTORY")
          );
          localStorage.setItem(
            localStorageName("SELECTED_REWARD_FROM_ORDER_HISTORY"),
            JSON.stringify([item])
          );
          this.$router.go(-1);
        } else {
          this.selectedReward = item;
          this.handleUseVoucher();
        }
      }
    },
    removeVoucherSelected() {
      this.selectedReward = null;
      this.rewardIdSelect = 0;
    },

    showUpdateProfile() {
      this.keyReloadModal = Math.random();
      $(".show-modal-update-profile").click();
    },

    async getDataMember() {
      try {
        const response = await memberService.getMemberInfo(this.merchantCode);

        if (response.data.result.isSuccess) {
          this.currentMember = response.data.data.member;
          this.memberPhone = this.currentMember.phone_number;
        } else {
          this.snackbar = "danger";
          this.message = response.data.result.message;
          this.setTimeOutNotification();
        }
      } catch (error) {
        this.snackbar = "danger";
        this.message = error;
        this.setTimeOutNotification();
      }
      this.hideLoader();
    },
  },
};
</script>
<style>
.title-reward-page {
  width: 60%;
  color: black;
  text-align: center;
}
.reward-card {
  border: 1px solid lightgray;
  border-radius: 5px;
  padding: 5px 10px;
}
.reward-info {
  position: relative;
}
.btn-apply,
.btn-remove {
  font-size: 14px;
  color: white;
  border-radius: 5px;
  background-color: var(--color);
  padding: 5px 15px;
  margin-right: 12px;
}
.btn-remove {
  background-color: orangered;
}
.text-bold-500 {
  color: black;
  font-weight: 500;
}
.text-bold-400 {
  color: black;
  font-weight: 400;
}
.progress-bar {
  height: 20px;
  width: 100%;
}
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  background-color: white;
  overflow: auto;
}
footer {
  background-color: white;
  padding-top: 50px;
}
.custom-link span {
  text-decoration: underline;
  color: #1a73e8 !important;
  cursor: pointer;
}
.btn-reward-catalogue {
  background-color: var(--color);
  color: white;
  text-align: center;
  border-radius: 6px;
  padding: 8px;
  font-weight: 600;
  cursor: pointer;
}

/* ── MTH035 theme2 reskin — Advocado "Available Vouchers" (VouchersModal).
   Every rule is confined to .moo-reward-page so the reward/list classes
   shared with RewardCatalogue / RewardDetail / Profile stay untouched.
   Colours are derived from the merchant theme var(--color): solid for the
   "dark" accents (title, buttons, badge) and color-mix opacity tiers for the
   light card tints, so the whole page tracks each merchant's brand. ── */
.moo-reward-page {
  --rw: var(--color);
  --rw-06: color-mix(in srgb, var(--color) 6%, #ffffff);
  --rw-09: color-mix(in srgb, var(--color) 9%, #ffffff);
  --rw-12: color-mix(in srgb, var(--color) 12%, #ffffff);
  --rw-15: color-mix(in srgb, var(--color) 15%, #ffffff);
  --rw-18: color-mix(in srgb, var(--color) 18%, #ffffff);
  --rw-22: color-mix(in srgb, var(--color) 22%, #ffffff);
  --rw-border: color-mix(in srgb, var(--color) 22%, #ffffff);
  --rw-muted: color-mix(in srgb, var(--color) 55%, #ffffff);
  --rw-inset: color-mix(in srgb, var(--color) 7%, transparent);
  font-family: "Plus Jakarta Sans", "Manrope", system-ui, sans-serif;
  background: color-mix(in srgb, var(--color) 6.75%, #ffffff);
}
.moo-reward-page .content-custom {
  background: color-mix(in srgb, var(--color) 6.75%, #ffffff);
}
/* Sheet — white rounded card with the brand badge on its top edge */
.moo-reward-page .reward-sheet {
  position: relative;
  width: 100%;
  max-width: 480px;
  margin: 48px auto 0;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  padding: 56px 20px 28px;
  min-height: calc(100% - 48px);
  box-shadow: 0 -12px 30px rgba(25, 28, 30, 0.06);
}
.moo-reward-page .reward-sheet-logo {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 80px;
  border-radius: 9999px;
  border: 4px solid #ffffff;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 8px 16px rgba(25, 28, 30, 0.12);
}
.moo-reward-page .reward-sheet-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* Header */
.moo-reward-page .reward-sheet-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
.moo-reward-page .reward-sheet-head-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.moo-reward-page .reward-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.moo-reward-page .reward-title-icon {
  color: var(--rw);
  font-size: 1.5rem;
}
.moo-reward-page .reward-title {
  font-weight: 800;
  font-size: 1.25rem;
  color: var(--rw);
  margin: 0;
  letter-spacing: -0.01em;
}
.moo-reward-page .reward-subtitle {
  color: #6b6868;
  font-size: 0.75rem;
  font-weight: 500;
  margin: 0;
}
.moo-reward-page .reward-close {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 9999px;
  border: none;
  background: var(--rw-inset);
  color: #6b6868;
  cursor: pointer;
}

.moo-reward-page .reward-close .material-icons-round {
  font-size: 18px;
  line-height: 1;
}
.moo-reward-page .reward-close:hover {
  background: var(--rw-inset);
}
/* Reward Catalogue — dark pill CTA */
.moo-reward-page .reward-catalogue-wrap {
  margin-top: 1rem;
}
.moo-reward-page .btn-reward-catalogue {
  background: var(--rw);
  color: #ffffff;
  border-radius: 9999px;
  padding: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.moo-reward-page .reward-search {
  display: block;
  margin-top: 0.85rem;
  /* MTH084: SearchInput.vue tự set --color từ moo_color legacy bằng inline
     style, đè lên màu Theme 2 kế thừa => underline lúc focus ra màu cũ.
     Trỏ lại về --rw; cần !important để thắng inline style. */
  --color: var(--rw) !important;
}
/* MTH084: viền lúc chưa focus — xám mặc định #d3d3d3 -> tint theme 2 */
.moo-reward-page .reward-search input.nosubmit {
  border-bottom-color: var(--rw-border);
}
/* Voucher list */
.moo-reward-page .reward-list {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.moo-reward-page .reward-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: var(--rw-06);
  border: 1px solid var(--rw-border);
}
.moo-reward-page .reward-card--ineligible {
  opacity: 0.6;
}
.moo-reward-page .reward-not-allowed-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: #fdecea;
  color: #b3261e;
  font-size: 0.9rem;
}
/* Card tints — graded opacity tiers of the merchant theme colour (đậm/nhạt),
   replacing the fixed multi-hue palette. */
.moo-reward-page .reward-card--welcome {
  background: var(--rw-22);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--birthday {
  background: var(--rw-18);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--digitalcash {
  background: var(--rw-15);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--other-1 {
  background: var(--rw-06);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--other-2 {
  background: var(--rw-09);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--other-3 {
  background: var(--rw-12);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--other-4 {
  background: var(--rw-15);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card--other-5 {
  background: var(--rw-18);
  border-color: var(--rw-border);
}
.moo-reward-page .reward-card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.moo-reward-page .reward-chip {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--rw-muted);
  text-transform: uppercase;
  letter-spacing: 0.15em;
}
.moo-reward-page .reward-use {
  background: var(--rw);
  color: #ffffff;
  flex: 0 0 auto;
  width: 100px;
  min-width: 100px;
  box-sizing: border-box;
  padding: 0.5rem 0.25rem;
  border-radius: 0.75rem;
  border: none;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  text-align: center;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.moo-reward-page .reward-use:hover {
  opacity: 0.9;
}
.moo-reward-page .reward-use:active {
  transform: scale(0.96);
}
.moo-reward-page .reward-use--applied {
  background: #ba1a1a;
}
.moo-reward-page .reward-use--ineligible {
  background: #b3b3b3;
  color: rgba(255, 255, 255, 0.85);
  cursor: not-allowed;
}
.moo-reward-page .reward-card-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.moo-reward-page .reward-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1a1717;
  margin: 0;
  overflow-wrap: anywhere;
}
.moo-reward-page .reward-meta {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b6868;
  font-style: italic;
}
/* MTH086: "N available · M used" line on stacked cards */
.moo-reward-page .reward-stack-availability {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--rw-muted);
}
/* MTH091: "add $X more" hint on a voucher whose minimum spend isn't met.
   Uses the full-strength text colour, not --rw-muted: the card it sits in is
   already dimmed to 0.6, and this line is the reason it is dimmed. */
.moo-reward-page .reward-min-spend-note {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--rw);
}
/* T&C nested panel */
.moo-reward-page .reward-tnc {
  margin-top: 0.25rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.625rem;
  background: var(--rw-inset);
  color: var(--rw);
  font-size: 0.75rem;
  line-height: 1.4;
}
.moo-reward-page .reward-tnc-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--rw-muted);
}
.moo-reward-page .reward-tnc-list {
  margin: 0;
  padding-left: 1rem;
  list-style: disc;
}
.moo-reward-page .reward-tnc-more {
  margin-top: 0.35rem;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
/* xN count badge — bottom-left of the card */
.moo-reward-page .reward-stack-badge {
  align-self: flex-start;
  min-width: 1.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  background: var(--rw);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.02em;
  text-align: center;
  box-shadow: 0 2px 6px rgba(25, 28, 30, 0.22);
}
/* Empty state */
.moo-reward-page .reward-empty {
  margin-top: 1rem;
  padding: 2.5rem 1rem;
  text-align: center;
  color: #6b6868;
  background: var(--rw-06);
  border-radius: 1rem;
  font-weight: 600;
}
/* Update-profile row */
.moo-reward-page .root_row {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid var(--rw-border);
  border-radius: 16px;
  padding: 12px 14px;
  margin-top: 16px;
  cursor: pointer;
}
.moo-reward-page .root_row_child {
  display: flex;
  align-items: center;
  color: var(--rw);
}
.moo-reward-page .root_row_child_text {
  flex: 1;
  margin-left: 10px;
  font-weight: 700;
  color: #201a1a;
}
.moo-reward-page .root_row_child_icon {
  color: #6b6868;
}
/* ---- terms & conditions sheet ----
   Same treatment RewardCatalogue's terms sheet got, expressed in this screen's
   own --rw-* tokens. Slide-in and positioning come from .moo-bottom-sheet in
   moo-sheet.css; the plain Bootstrap dialog this replaced dropped in from the
   top of the screen and closed with a stock pink btn-primary that ignored the
   merchant colour entirely.

   NOTE: this file's style block is not scoped, unlike every other screen's.
   Hence
   the .moo-reward-page prefix on each rule (the convention the rest of this
   block already follows) and plain descendant selectors instead of :deep(),
   which only exists inside a scoped block. */
.moo-reward-page .rw-terms {
  border: 0;
  border-radius: 24px 24px 0 0;
  padding: 0 20px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -12px 30px rgba(25, 28, 30, 0.12);
  font-family: "Plus Jakarta Sans", "Manrope", system-ui, sans-serif;
}
.moo-reward-page .rw-terms::before {
  content: "";
  display: block;
  width: 36px;
  height: 4px;
  margin: 8px auto 0;
  border-radius: 999px;
  background: var(--rw-border);
}
.moo-reward-page .rw-terms__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0 12px;
}
.moo-reward-page .rw-terms__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: var(--rw-12);
  color: var(--rw);
  font-size: 20px;
}
.moo-reward-page .rw-terms__title {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 800;
  color: #241a12;
}
.moo-reward-page .rw-terms__close {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: var(--rw-inset);
  color: #6b6868;
  cursor: pointer;
}
.moo-reward-page .rw-terms__close .material-icons-round {
  font-size: 18px;
}
.moo-reward-page .rw-terms__body {
  /* Merchant terms run to any length; the sheet stays put and the text scrolls
     inside it rather than pushing the Close button off screen. */
  max-height: 46vh;
  overflow-y: auto;
  background: var(--rw-06);
  border: 1px solid var(--rw-border);
  border-radius: 14px;
  padding: 14px;
  font-size: 13.5px;
  line-height: 1.65;
  color: #4a4342;
  /* terms_conditions arrives as merchant-typed text with bare newlines. */
  white-space: pre-line;
  overflow-wrap: anywhere;
}
/* v-html content is outside scoped-style reach without . */
.moo-reward-page .rw-terms__body p {
  margin: 0 0 8px;
}
.moo-reward-page .rw-terms__body p:last-child {
  margin-bottom: 0;
}
.moo-reward-page .rw-terms__body ul,
.moo-reward-page .rw-terms__body ol {
  margin: 0 0 8px;
  padding-left: 18px;
}
.moo-reward-page .rw-terms__body a {
  color: var(--rw);
  font-weight: 600;
  text-decoration: underline;
}
.moo-reward-page .rw-terms__done {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  border: 0;
  border-radius: 9999px;
  background: var(--rw);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
  cursor: pointer;
  transition: transform 0.1s ease;
}
.moo-reward-page .rw-terms__done:active {
  transform: scale(0.98);
}
</style>
